import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckCheckIcon, CheckIcon, XIcon } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import type { AttendanceMark, Voice } from '../../types';
import { eventTitle, eventTypeClass, eventTypeShort, formatDate, fullName, voiceLabel, voiceOrder } from '../../utils/labels';
export function AdminAttendanceTake() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    events,
    expectedFor,
    attendance,
    saveAttendance,
    rsvps,
    pushToast
  } = useApp();
  const event = events.find(e => e.id === id);
  const pricakovani = useMemo(() => event ? expectedFor(event) : [], [event, expectedFor]);
  const [marks, setMarks] = useState<Record<string, AttendanceMark>>(() => {
    if (!event) return {};
    const shranjeno = attendance[event.id];
    if (shranjeno) return {
      ...shranjeno
    };
    const zacetno: Record<string, AttendanceMark> = {};
    expectedFor(event).forEach(m => {
      zacetno[m.id] = rsvps[`${m.id}:${event.id}`]?.status === 'ne_morem' ? 'odsoten' : 'prisoten';
    });
    return zacetno;
  });
  if (!event) {
    return <Card className="p-10 text-center" data-id="element-650" data-mp="1rofbh1mdycy4">
        <p className="text-sm font-medium text-ink" data-id="element-651" data-mp="1wmqkja4uwjfa">Dogodek ne obstaja.</p>
        <Link to="/admin/prisotnost" className="mt-2 inline-block text-[13px] font-medium text-green-600" data-id="element-652" data-mp="1wol4om8b07ib">
          Nazaj
        </Link>
      </Card>;
  }
  const poGlasovih = voiceOrder.map(v => ({
    voice: v as Voice,
    clani: pricakovani.filter(m => m.voice === v)
  })).filter(g => g.clani.length > 0);
  const prisotnih = pricakovani.filter(m => marks[m.id] !== 'odsoten').length;
  const odsotnih = pricakovani.length - prisotnih;
  function nastavi(memberId: string, mark: AttendanceMark) {
    setMarks(m => ({
      ...m,
      [memberId]: mark
    }));
  }
  function vsiPrisotni() {
    const novi: Record<string, AttendanceMark> = {};
    pricakovani.forEach(m => {
      novi[m.id] = 'prisoten';
    });
    setMarks(novi);
  }
  return <div className="mx-auto max-w-4xl space-y-4 pb-24" data-id="element-653" data-mp="1skm8v648qdii">
      <button onClick={() => navigate('/admin/prisotnost')} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-654" data-mp="1ohzasngz7wcv">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-655" data-mp="1xjflyxfm82i5" />
        Nazaj na prisotnost
      </button>

      <Card className="p-5" data-id="element-656" data-mp="2c81ykrduyg5o">
        <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-657" data-mp="2c68r59vf7rwm">
          <div data-id="element-658" data-mp="277xk16wifvi3">
            <div className="flex flex-wrap items-center gap-1.5" data-id="element-659" data-mp="279icmqz9l4db">
              <Badge className={eventTypeClass[event.type]} data-id="element-660" data-mp="2l7ysb7f3u57l">{eventTypeShort[event.type]}</Badge>
              {event.opomba && <Badge className="bg-canvas text-ink-muted border-line" data-id="element-661" data-mp="2g9ngws95vmn9">{event.opomba}</Badge>}
            </div>
            <h1 className="mt-2 text-xl font-semibold text-ink" data-id="element-662" data-mp="2zvyuf5r2fk3i">{eventTitle(event)}</h1>
            <p className="mt-1 text-[13px] text-ink-muted" data-id="element-663" data-mp="34ujsayiuhros">
              {formatDate(event.datum, {
              weekday: true
            })} · {event.zacetek} · {event.lokacija}
            </p>
          </div>
          <Button variant="secondary" onClick={vsiPrisotni} data-id="element-664" data-mp="2uvklllhn464f">
            <CheckCheckIcon className="h-4 w-4" data-id="element-665" data-mp="2uxxc4n1grot3" />
            Označi vse prisotne
          </Button>
        </div>
      </Card>

      <div className="space-y-3" data-id="element-666" data-mp="2lv5xkedakcx8">
        {poGlasovih.map(({
        voice,
        clani
      }) => <Card key={voice} className="overflow-hidden" data-id="element-667" data-mp="2lwz6y1fahb8x">
            <div className="flex items-center justify-between border-b border-line bg-canvas px-4 py-2.5" data-id="element-668" data-mp="277kugkgf2dpb">
              <h2 className="text-[13px] font-semibold uppercase tracking-wide text-ink" data-id="element-669" data-mp="2c5w4owamiq82">
                {voiceLabel[voice]}
              </h2>
              <span className="text-[12px] text-ink-muted" data-id="element-670" data-mp="234ogx85qh7s8">
                {clani.filter(m => marks[m.id] !== 'odsoten').length}/{clani.length} prisotnih
              </span>
            </div>
            <ul className="divide-y divide-line" data-id="element-671" data-mp="1q4vu1kswqlrj">
              {clani.map(m => {
            const napovedana = rsvps[`${m.id}:${event.id}`]?.status === 'ne_morem';
            const mark = marks[m.id] ?? 'prisoten';
            return <li key={m.id} className="flex items-center gap-3 px-4 py-2.5" data-id="element-672" data-mp="1hc2cfgpznaai">
                    <span className="min-w-0 flex-1" data-id="element-673" data-mp="1ceilbg86z315">
                      <span className="block truncate text-sm font-medium text-ink" data-id="element-674" data-mp="17ee4fp3dg6ae">{fullName(m)}</span>
                      {napovedana && <span className="mt-0.5 inline-flex text-[12px] text-amber-700" data-id="element-675" data-mp="12gcld45y46yx">
                          Napovedana odsotnost
                          {rsvps[`${m.id}:${event.id}`]?.razlog ? ` — ${rsvps[`${m.id}:${event.id}`]?.razlog?.toLowerCase()}` : ''}
                        </span>}
                    </span>
                    <div className="flex shrink-0 gap-1.5" data-id="element-676" data-mp="ej6ka8krhoz2">
                      <button onClick={() => nastavi(m.id, 'prisoten')} aria-pressed={mark === 'prisoten'} aria-label={`${fullName(m)} prisoten`} className={`flex h-10 w-14 items-center justify-center rounded-lg border transition-colors duration-150 ease-out ${mark === 'prisoten' ? 'border-green-500 bg-green-500 text-white' : 'border-line bg-white text-ink-muted hover:bg-canvas'}`} data-id="element-677" data-mp="jisxii0lb57v">
                        <CheckIcon className="h-4 w-4" data-id="element-678" data-mp="9lekksdvkykb" />
                      </button>
                      <button onClick={() => nastavi(m.id, 'odsoten')} aria-pressed={mark === 'odsoten'} aria-label={`${fullName(m)} odsoten`} className={`flex h-10 w-14 items-center justify-center rounded-lg border transition-colors duration-150 ease-out ${mark === 'odsoten' ? 'border-rose-500 bg-rose-500 text-white' : 'border-line bg-white text-ink-muted hover:bg-canvas'}`} data-id="element-679" data-mp="1g17a63cxt4ej">
                        <XIcon className="h-4 w-4" data-id="element-680" data-mp="2yc5ns3kd9psa" />
                      </button>
                    </div>
                  </li>;
          })}
            </ul>
          </Card>)}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-white px-4 py-3 lg:left-64" data-id="element-681" data-mp="383jryon9jdrr">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4" data-id="element-682" data-mp="33aql9v88hvd1">
          <div className="text-sm" data-id="element-683" data-mp="2flfhs5zi1mv7">
            <span className="font-semibold text-green-600" data-id="element-684" data-mp="2fkcue81dy2k1">{prisotnih} prisotnih</span>
            <span className="mx-2 text-line" data-id="element-685" data-mp="2aml2vf4nkigd">·</span>
            <span className="font-semibold text-rose-600" data-id="element-686" data-mp="q5btzoscnavc">{odsotnih} odsotnih</span>
            <span className="ml-2 text-ink-muted" data-id="element-687" data-mp="uvs8zdbtj4jt">od {pricakovani.length}</span>
          </div>
          <Button onClick={() => {
          saveAttendance(event.id, marks);
          pushToast('Prisotnost je shranjena.');
          navigate('/admin/prisotnost');
        }} data-id="element-688" data-mp="uxmxt2q7ngt6">
            Shrani prisotnost
          </Button>
        </div>
      </div>
    </div>;
}